import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { reportError } from '@/utils/sentry/sentryReporter.ts';

/**
 * Extracts the HTTP status code from an error object.
 * Handles AxiosError objects with response.status property.
 */
const getStatusCode = (error: Error): number | null => {
	if ('response' in error && error.response && typeof error.response === 'object') {
		const axiosError = error as AxiosError;
		return axiosError.response?.status ?? null;
	}
	return null;
};

/**
 * Global error handler for all queries.
 * Reports server errors (500+) to Sentry while allowing errors to propagate.
 */
const handleQueryError = (error: Error) => {
	const statusCode = getStatusCode(error);

	// Only report server errors (500s), not client errors (400s)
	if (statusCode && statusCode >= 500) {
		reportError(error, {
			tags: {
				type: 'api_error',
				status_code: statusCode.toString(),
				error_type: 'query',
			},
		});
	}
};

/**
 * Global error handler for all mutations.
 * Reports server errors (500+) to Sentry while allowing errors to propagate.
 */
const handleMutationError = (error: Error) => {
	const statusCode = getStatusCode(error);

	// Report all mutation server errors (they're user actions that failed)
	if (statusCode && statusCode >= 500) {
		reportError(error, {
			tags: {
				type: 'api_error',
				status_code: statusCode.toString(),
				error_type: 'mutation',
			},
		});
	}
};

export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: handleQueryError,
	}),
	mutationCache: new MutationCache({
		onError: handleMutationError,
	}),
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000,
			retry: 1,
		},
	},
});
